import { Spinner } from "@/components/ui/spinner";
import { useState } from "react";
import { CalendarIcon, X } from "lucide-react";
import { dayjs, ExpenseCategory, RecurringFrequency } from "@boo/shared";
import type { RecurringExpenseItem } from "@boo/shared";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useRecurringExpenseStore } from "../store/useRecurringExpenseStore";
import AmountInput from "@/components/AmountInput";

const CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  [ExpenseCategory.FOOD]: "Food",
  [ExpenseCategory.TRANSPORT]: "Transport",
  [ExpenseCategory.HOUSING]: "Housing",
  [ExpenseCategory.ENTERTAINMENT]: "Entertainment",
  [ExpenseCategory.HEALTH]: "Health",
  [ExpenseCategory.FINANCIAL]: "Financial",
  [ExpenseCategory.INVESTMENT]: "Investment",
  [ExpenseCategory.SHOPPING]: "Shopping",
  [ExpenseCategory.BILLS]: "Bills",
  [ExpenseCategory.OTHER]: "Other",
};

const EXPENSE_CATEGORIES = [
  ...Object.values(ExpenseCategory).filter((c) => c !== ExpenseCategory.OTHER),
  ExpenseCategory.OTHER,
] as ExpenseCategory[];

const FREQ_OPTIONS = [
  { value: RecurringFrequency.WEEKLY, label: "Weekly" },
  { value: RecurringFrequency.MONTHLY, label: "Monthly" },
  { value: RecurringFrequency.QUARTERLY, label: "Quarterly" },
  { value: RecurringFrequency.YEARLY, label: "Yearly" },
] as const;

const INPUT_CLASS =
  "w-full rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border";

interface Props {
  onClose: () => void;
  initialValues?: RecurringExpenseItem;
}

function parseInitialDate(isoString: string): Date {
  return dayjs(isoString).toDate();
}

export default function RecurringExpenseModal({
  onClose,
  initialValues,
}: Props) {
  const { createExpense, updateExpense } = useRecurringExpenseStore();
  const isEdit = !!initialValues;

  const [title, setTitle] = useState(initialValues?.title ?? "");
  const [amount, setAmount] = useState(
    initialValues ? String(initialValues.amount) : "",
  );
  const [category, setCategory] = useState<ExpenseCategory>(
    (initialValues?.category as ExpenseCategory) ?? ExpenseCategory.BILLS,
  );
  const [frequency, setFrequency] = useState<RecurringFrequency>(
    (initialValues?.frequency as RecurringFrequency) ??
      RecurringFrequency.MONTHLY,
  );
  const [dueDate, setDueDate] = useState<Date | undefined>(
    initialValues ? parseInitialDate(initialValues.nextDueDate) : new Date(),
  );
  const [calOpen, setCalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!title.trim()) next.title = "Name is required.";
    const amt = parseFloat(amount);
    if (!amount || isNaN(amt) || amt <= 0)
      next.amount = "Amount must be greater than 0.";
    if (!dueDate) next.dueDate = "Next due date is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate() || !dueDate) return;

    const d = dayjs(dueDate);
    const dayOfMonth = Math.min(d.date(), 28);
    const month = d.month() + 1;

    const payload = {
      title: title.trim(),
      amount: parseFloat(amount),
      category,
      frequency,
      dayOfMonth,
      month: frequency === RecurringFrequency.YEARLY ? month : undefined,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await updateExpense(initialValues.id, payload);
      } else {
        await createExpense(payload);
      }
      onClose();
    } catch (err) {
      setErrors({
        submit: err instanceof Error ? err.message : "Failed to save.",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-sm mx-4 rounded-[16px] bg-card border-[0.5px] border-border shadow-xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[15px] font-semibold text-foreground">
            {isEdit ? "Edit recurring expense" : "Add recurring expense"}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Name */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Name
            </label>
            <input
              className={INPUT_CLASS}
              placeholder="e.g. Netflix"
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            {errors.title && (
              <p className="text-[12px] text-red-500">{errors.title}</p>
            )}
          </div>

          {/* Amount */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Amount (฿)
            </label>
            <AmountInput
              className={`${INPUT_CLASS} [font-variant-numeric:tabular-nums]`}
              placeholder="0"
              value={amount}
              onChange={setAmount}
            />
            {errors.amount && (
              <p className="text-[12px] text-red-500">{errors.amount}</p>
            )}
          </div>

          {/* Category */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Category
            </label>
            <select
              className={INPUT_CLASS}
              value={category}
              onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
            >
              {EXPENSE_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {CATEGORY_LABELS[cat]}
                </option>
              ))}
            </select>
          </div>

          {/* Frequency */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Frequency
            </label>
            <div className="grid grid-cols-2 gap-2">
              {FREQ_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFrequency(value)}
                  className={`rounded-[8px] border-[0.5px] py-2 text-[12px] font-medium transition-colors cursor-pointer ${
                    frequency === value
                      ? "border-foreground bg-foreground text-[var(--page-bg)]"
                      : "border-border bg-[var(--surface-muted)] text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Next due date */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Next due date
            </label>
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`flex items-center justify-between rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-3 py-2 text-[13px] transition-colors cursor-pointer ${
                    dueDate ? "text-foreground" : "text-muted-foreground"
                  } hover:border-foreground/30 focus:outline-none focus:ring-1 focus:ring-border`}
                >
                  <span>
                    {dueDate
                      ? dayjs(dueDate).format("D MMM YYYY")
                      : "Pick a date"}
                  </span>
                  <CalendarIcon className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0 bg-card border-[0.5px] border-border shadow-xl rounded-[12px] overflow-hidden"
                align="start"
              >
                <Calendar
                  mode="single"
                  selected={dueDate}
                  defaultMonth={dueDate}
                  onSelect={(date) => {
                    setDueDate(date);
                    setCalOpen(false);
                  }}
                  autoFocus
                />
              </PopoverContent>
            </Popover>
            {errors.dueDate && (
              <p className="text-[12px] text-red-500">{errors.dueDate}</p>
            )}
          </div>

          {errors.submit && (
            <p className="text-[12px] text-red-500">{errors.submit}</p>
          )}

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-1.5 w-full rounded-[8px] bg-foreground text-[var(--page-bg)] py-2.5 text-[13px] font-semibold hover:opacity-80 transition-opacity disabled:opacity-40 cursor-pointer"
          >
            {saving && <Spinner className="size-3.5" />}
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add expense"}
          </button>
        </form>
      </div>
    </div>
  );
}
